import { Component, type ReactNode } from "react";

/**
 * Vangt fouten bij het lui laden van route-chunks. Na een nieuwe deploy zijn de oude
 * chunk-bestanden (met oude hash) van de hosting verdwenen; navigeer je dan met een
 * tab die al openstond, dan faalt de dynamische import → wit scherm. Dan herladen we
 * één keer (met loop-guard) zodat de nieuwe index.html + chunks geladen worden.
 *
 * Een wit scherm is nooit het eindstation. Lukt herladen niet (tweede fout binnen de
 * afkoelperiode) of gaat het om een heel andere fout, dan komt er een leesbaar bericht met een
 * knop. Zonder dat bleef de bezoeker op een lege pagina hangen met als enige uitweg het tabblad
 * sluiten — en zag je als beheerder nooit wat er misging.
 */
const RELOAD_KEY = "gt:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 30_000;

/** Ziet de fout eruit als een mislukte dynamische import (stale chunk na deploy)? */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err ?? "");
  return /ChunkLoadError|dynamically imported module|Importing a module script failed|Failed to fetch dynamically|error loading dynamically imported|Unable to preload/i.test(msg);
}

/** Herlaad één keer per cooldown-venster; voorkomt een reload-loop bij een échte 404. */
export function reloadOnceForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last > RELOAD_COOLDOWN_MS) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return true;
    }
  } catch {
    window.location.reload();
    return true;
  }
  return false;
}

interface State {
  failed: boolean;
  reloading: boolean;
  /** Korte technische omschrijving — uitklapbaar op het scherm, zodat een melding bruikbaar is. */
  detail: string;
}

export class RouteErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false, reloading: false, detail: "" };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? "onbekende fout");
    return { failed: true, detail: msg.slice(0, 300) };
  }

  override componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      this.setState({ reloading: true });
      return;
    }
    // Niet-herstelbaar (of al een keer geprobeerd): in de console laten staan, zodat een melding
    // van een bezoeker later terug te vinden is.
    console.error("[route] pagina kon niet geladen worden", error);
  }

  override render(): ReactNode {
    // Herladen loopt al: een rustige laadstaat, geen melding die opflitst.
    if (this.state.reloading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-white" role="status" aria-live="polite">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" aria-hidden />
          <span className="sr-only">Bezig met laden…</span>
        </div>
      );
    }
    if (this.state.failed) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center">
          <h1 className="text-xl font-bold text-primary-900">Deze pagina kon niet geladen worden</h1>
          <p className="max-w-md text-sm text-primary-600">
            Meestal is er net een nieuwe versie van de site uitgekomen. Herladen lost het bijna
            altijd op; uw winkelwagen blijft bewaard.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="min-h-11 rounded-lg bg-accent-700 px-5 font-semibold text-white transition hover:bg-accent-800"
            >
              Pagina herladen
            </button>
            <a
              href="/"
              className="flex min-h-11 items-center rounded-lg border border-primary-300 px-5 font-semibold text-primary-700 transition hover:bg-primary-50"
            >
              Naar de homepage
            </a>
          </div>
          {/* Uitgeklapt is dit precies wat we nodig hebben om de fout te vinden; dichtgeklapt
              stoort het niemand. */}
          <details className="mt-2 max-w-lg text-left">
            <summary className="cursor-pointer text-xs text-primary-600">Technische details</summary>
            <p className="mt-1 break-words rounded-lg bg-primary-50 p-3 text-xs text-primary-700">
              {window.location.pathname} — {this.state.detail || "geen details"}
            </p>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}
